import { s } from "../style.js";
import type React from "react";
import type { Page, Role } from "../types";

const ink = (on: boolean) => (on ? "var(--on-accent)" : "var(--rail-ink)");
const bg = (on: boolean) => (on ? "var(--color-accent)" : "transparent");

const labelStyle = s(
  "font-size:9px; letter-spacing:0.08em; text-transform:uppercase; font-family:var(--font-heading); font-weight:800"
);

function RailButton({ title, active, onClick, railLabels, label, children }: { title: string; active: boolean; onClick: () => void; railLabels: boolean; label: string; children: React.ReactNode }) {
  return (
    <button
      className="rail-btn"
      onClick={onClick}
      title={title}
      style={{
        ...s(
          "all:unset; cursor:pointer; height:80px; flex:none; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px"
        ),
        color: ink(active),
        background: bg(active),
      }}
    >
      {children}
      {railLabels && <span style={labelStyle}>{label}</span>}
    </button>
  );
}

export default function Rail({ page, railLabels, isDark, auth, onNavigate, onToggleTheme, onLogout }: { page: Page; railLabels: boolean; isDark: boolean; auth: Role; onNavigate: (page: Page) => void; onToggleTheme: () => void; onLogout: () => void }) {
  return (
    <nav
      style={s(
        "width:80px; flex:none; background:var(--rail-bg); display:flex; flex-direction:column; align-items:stretch"
      )}
    >
      <RailButton
        title="Ops Overview"
        active={page === "home"}
        onClick={() => onNavigate("home")}
        railLabels={railLabels}
        label="Home"
      >
        <span
          style={s(
            "font-family:var(--font-heading); font-weight:800; font-size:30px; letter-spacing:-0.04em"
          )}
        >
          IT
        </span>
      </RailButton>

      <RailButton
        title="Sprint Overview"
        active={page === "sprint"}
        onClick={() => onNavigate("sprint")}
        railLabels={railLabels}
        label="Sprint"
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
          <rect width="7" height="9" x="3" y="3" />
          <rect width="7" height="5" x="14" y="3" />
          <rect width="7" height="9" x="14" y="12" />
          <rect width="7" height="5" x="3" y="16" />
        </svg>
      </RailButton>

      <RailButton
        title="Platform Status"
        active={page === "server"}
        onClick={() => onNavigate("server")}
        railLabels={railLabels}
        label="Platforms"
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
          <rect width="20" height="8" x="2" y="2" />
          <rect width="20" height="8" x="2" y="14" />
          <line x1="6" x2="6.01" y1="6" y2="6" />
          <line x1="6" x2="6.01" y1="18" y2="18" />
        </svg>
      </RailButton>

      <RailButton
        title="Code Review Queue"
        active={page === "review"}
        onClick={() => onNavigate("review")}
        railLabels={railLabels}
        label="Review"
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
          <circle cx="18" cy="18" r="3" />
          <circle cx="6" cy="6" r="3" />
          <path d="M13 6h3a2 2 0 0 1 2 2v7" />
          <line x1="6" x2="6" y1="9" y2="21" />
        </svg>
      </RailButton>

      <RailButton
        title="Estimate"
        active={page === "estimate"}
        onClick={() => onNavigate("estimate")}
        railLabels={railLabels}
        label="Estimate"
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
        </svg>
      </RailButton>

      {auth === "admin" && (
        <>
          <div style={s("border-top:2px solid rgba(243,242,242,0.18)")} />
          <RailButton
            title="Configuration"
            active={page === "config"}
            onClick={() => onNavigate("config")}
            railLabels={railLabels}
            label="Config"
          >
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 0 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </RailButton>
        </>
      )}

      <div style={s("flex:1")} />

      <button
        className="rail-btn"
        onClick={onLogout}
        title="Sign out"
        style={{
          ...s(
            "all:unset; cursor:pointer; height:80px; flex:none; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; border-top:2px solid rgba(243,242,242,0.18)"
          ),
          color: "var(--rail-ink)",
        }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <path d="M16 17l5-5-5-5" />
          <line x1="21" x2="9" y1="12" y2="12" />
        </svg>
        {railLabels && <span style={labelStyle}>Out</span>}
      </button>

      <button
        className="rail-btn"
        onClick={onToggleTheme}
        title="Toggle dark mode"
        style={{
          ...s(
            "all:unset; cursor:pointer; height:80px; flex:none; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px; border-top:2px solid rgba(243,242,242,0.18)"
          ),
          color: "var(--rail-ink)",
        }}
      >
        {isDark ? (
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1" />
          </svg>
        ) : (
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
            <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" />
          </svg>
        )}
        {railLabels && <span style={labelStyle}>Theme</span>}
      </button>
    </nav>
  );
}
